import { useCallback, useMemo, useState } from 'react';
import { statusCategories } from '@atlassian/jira-common-constants';
import { useCalendarIssues as useCalendarIssuesService } from '../../services/calendar-view-items';
import type { UseCalendarItemsRequest } from '../../services/calendar-view-items/types';
import type {
	CalendarIssue,
	CalendarDataSourceItem,
	CalendarDataSourceHook,
	SetOptimisticItem,
	Refetch,
} from '../../types';
import { issueToFullCalendarEvent } from './utils';

const EMPTY: CalendarDataSourceItem<CalendarIssue>[] = [];

export const useCalendarIssues = (request: UseCalendarItemsRequest): CalendarDataSourceHook => {
	const { loading, error, refetch, data } = useCalendarIssuesService(request);

	const [override, setOverride] = useState<CalendarDataSourceItem<CalendarIssue>[] | null>(null);
	const setOptimisticItem: SetOptimisticItem = useCallback(
		(newIssue) => {
			if (newIssue == null) {
				setOverride(null);
			} else {
				setOverride(data != null ? [...data, newIssue] : [newIssue]);
			}
		},
		[data],
	);

	const refetchFn: Refetch = useCallback(
		async (newIssue) => {
			if (newIssue) {
				setOptimisticItem(newIssue);
			}

			await refetch();

			// delete overrides after backend comes back with the data
			setOptimisticItem(null);
		},
		[setOptimisticItem, refetch],
	);

	const items: CalendarDataSourceItem<CalendarIssue>[] = override ?? data ?? EMPTY;

	const parsedIssues = useMemo(() => {
		const analyticsAttributes = {
			issuesInView: items.length,
			issuesInTodoStatus: 0,
			issuesInProgressStatus: 0,
			issuesInDoneStatus: 0,
		};

		const incrementStatusAttributes = (statusCategoryId: CalendarIssue['statusCategoryId']) => {
			const { statusCategoryForId, TODO, IN_PROGRESS, DONE } = statusCategories;
			const status = statusCategoryForId(statusCategoryId);
			switch (status) {
				case TODO:
					analyticsAttributes.issuesInTodoStatus += 1;
					break;
				case IN_PROGRESS:
					analyticsAttributes.issuesInProgressStatus += 1;
					break;
				case DONE:
					analyticsAttributes.issuesInDoneStatus += 1;
					break;
				default:
					break;
			}
		};

		// issues parsed to fullCalendarEvents
		const fullCalendarIssues = items.map((item) => {
			incrementStatusAttributes(item.statusCategoryId);
			return issueToFullCalendarEvent(item);
		});
		return {
			fullCalendarIssues,
			analyticsAttributes,
		};
	}, [items]);

	return {
		issues: parsedIssues.fullCalendarIssues,
		analyticsAttributes: parsedIssues.analyticsAttributes,
		loading,
		error,
		refetch: refetchFn,
		setOptimisticItem,
	};
};
